import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import {
  ISession,
  ICreateSessionRequest,
  IUpdateSessionRequest,
  ISessionsListParams,
  ISessionWithStats,
} from '../../common/interfaces/Session.interface';
import {
  IUserSession,
  IUpdateUserSessionStatusRequest,
} from '../../common/interfaces/UserSession.interface';
import { IPaginatedResponse } from '../../common/interfaces/PaginatedResponse.interface';
import { createBaseQuery } from './utils/createBaseQuery';

export const sessionsApi = createApi({
  reducerPath: 'sessionsApi',
  baseQuery: createBaseQuery(''),
  tagTypes: ['Session', 'UserSession'],
  endpoints: (builder) => ({
    createSession: builder.mutation<
      { code: HttpStatus; data: ISession },
      ICreateSessionRequest
    >({
      query: (newSession) => ({
        url: '/dashboard/sessions',
        method: 'POST',
        body: newSession,
      }),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),

    updateSession: builder.mutation<
      { code: HttpStatus; data: ISession },
      { id: string; updatedSession: IUpdateSessionRequest }
    >({
      query: ({ id, updatedSession }) => ({
        url: `/dashboard/sessions/${id}`,
        method: 'PUT',
        body: updatedSession,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),

    fetchSessions: builder.query<
      IPaginatedResponse<ISessionWithStats>,
      ISessionsListParams
    >({
      query: ({ page = 1, limit = 10, status, search }) => ({
        url: '/dashboard/sessions',
        params: {
          page,
          limit,
          ...(status && { status }),
          ...(search && { search }),
        },
      }),
      transformResponse: (response: {
        code: HttpStatus;
        data: {
          data: ISessionWithStats[];
          total: number;
          page: number;
          limit: number;
          nextCursor?: string;
        };
      }) => {
        const { data, total, page, limit } = response.data;
        const pageCount = Math.ceil(total / limit);
        return {
          data,
          meta: {
            page,
            take: limit,
            itemCount: total,
            pageCount,
            hasPreviousPage: page > 1,
            hasNextPage: page < pageCount,
          },
        };
      },
      providesTags: (result) =>
        result
          ? [
              { type: 'Session' as const, id: 'LIST' },
              ...result.data.map(({ id }) => ({
                type: 'Session' as const,
                id: id,
              })),
            ]
          : [{ type: 'Session', id: 'LIST' }],
    }),

    fetchSessionById: builder.query<
      { code: HttpStatus; data: ISession },
      string
    >({
      query: (id) => `/dashboard/sessions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Session', id }],
    }),

    fetchUserSessions: builder.query<
      { code: HttpStatus; data: IUserSession[] },
      void
    >({
      query: () => '/user/sessions',
      providesTags: (result) =>
        result
          ? [
              { type: 'UserSession' as const, id: 'LIST' },
              ...result.data.map(({ id }) => ({
                type: 'UserSession' as const,
                id: id,
              })),
            ]
          : [{ type: 'UserSession', id: 'LIST' }],
    }),

    updateUserSessionStatus: builder.mutation<
      { code: HttpStatus; data: IUserSession },
      { userSessionId: string; status: IUpdateUserSessionStatusRequest }
    >({
      query: ({ userSessionId, status }) => ({
        url: `/user/sessions/${userSessionId}/status`,
        method: 'PUT',
        body: status,
      }),
      invalidatesTags: (result, error, { userSessionId }) => [
        { type: 'UserSession', id: userSessionId },
        { type: 'UserSession', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useFetchSessionsQuery,
  useFetchSessionByIdQuery,
  useFetchUserSessionsQuery,
  useUpdateUserSessionStatusMutation,
} = sessionsApi;
